import { useState, useRef, useEffect, useCallback, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { Autocomplete, Chip, TextField } from "@mui/material";
import GavelIcon from '@mui/icons-material/Gavel';
import ViewInArIcon from '@mui/icons-material/ViewInAr';

import { useDispatch } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";
import { isEmpty, isEqual, isNull, isUndefined, replace } from "lodash";

import mpDemandService from "@plannel/services/mp/mp-demand-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import userService from "@plannel/services/system/user-service";

import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton, FilterButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import NumberUtil from "@plannel/utils/number-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import debug from "@plannel/utils/debug-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import PaginationContainer from "@plannel/components/PaginationContainer";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import dateUtils from "@plannel/utils/date-util";
import { green, grey } from "@mui/material/colors";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import SplitterContainer from "@plannel/components/SplitterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const PLANNING_RANGE = "PLANNING_RANGE";
const BUCKET_UNIT = "BUCKET_UNIT";
const { snackBarMessageColor, statusColor } = ColorUtil;
const { versionStatusCode, mpDemandType, buckets, roles, module, orderStatusCode, orderType } = CodeUtil;
const { RELEASED, PLANNED } = versionStatusCode;
const { MP } = module;
const { FORECAST, REPLENISHMENT, PRODORDER } = mpDemandType;

const STATUS_FILTER_DATA_OPTIONS = [
  orderStatusCode.ON_TIME, orderStatusCode.DELAY, orderStatusCode.SHORT
];

const CURRENT_DATE = new ZDate();
const START_MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
const END_MAX_DATE = new ZDate(CURRENT_DATE.getFullYear() + 10, CURRENT_DATE.getMonth() + 1, 0);
const INIT_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), CURRENT_DATE.getDate() + 120);
const PeriodFilter = ({ period, setPeriod }) => {
  const { t } = useTranslation();

  const onChange = (e, type) => {
    if (type) {
      if (type === "start") {
        e.setHours(0);
        e.setMinutes(0);
        e.setSeconds(0);
      } else if (type === "end") {
        e.setHours(23);
        e.setMinutes(59);
        e.setSeconds(59);
      }

      setPeriod({ ...period, [type]: e });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label={t("periodStart")}
        views={["year", "month", "day"]}
        format={"yyyy-MM-dd"}
        openTo="day"
        value={period.start}
        maxDate={period.end ? period.end : END_MAX_DATE}
        minDate={START_MIN_DATE}
        onAccept={e => onChange(e, "start")}
        // onClose={onChange}
        slotProps={{ textField: { InputProps: { color: "primary", size: "small", sx: { mr: 1.5, width: 200 } } } }}
      />
      <DatePicker
        label={t("periodEnd")}
        views={["year", "month", "day"]}
        format={"yyyy-MM-dd"}
        openTo="day"
        value={period.end}
        maxDate={END_MAX_DATE}
        minDate={period.start ? period.start : START_MIN_DATE}
        onAccept={e => onChange(e, "end")}
        // onClose={onChange}
        slotProps={{ textField: { InputProps: { color: "primary", size: "small", sx: { mr: 1.5, width: 200 } } } }}
      />
    </LocalizationProvider>
  );
};

const MpDemandAnalysis = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  const user = JSON.parse(localStorage.getItem("user"));
  const [userRoles, setUserRoles] = useState(reduxViewState?.userRoles ?? []);
  const planningRangeValue = useRef(120);
  const bucketUnitValue = useRef(buckets.DAY);

  /* Search Filter */
  const [mpDemandVersionFilter, setMpDemandVersionFilter] = useState(reduxViewState?.mpDemandVersion ? reduxViewState.mpDemandVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null });
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [demandType, setDemandType] = useState(reduxViewState?.demandType ?? null);
  const [period, setPeriod] = useState({ 
    start: new ZDate(CURRENT_DATE),
    end: new ZDate(INIT_END_DATE)
  });

  /* Version Filter List */
  const [mpDemandVersionList, setMpDemandVersionList] = useState([]);
  const [demandVersionModule, setDemandVersionModule] = useState("");

  /* Grid Data */
  const [mpDemandData, setMpDemandData] = useState([]);

  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? 100);
  const pageSizes = [50, 100, 200, 500];
  const [gridPages, setGridPages] = useState(1);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const mpDemandGridRef = useRef();
  const mpOrderGridRef = useRef();
  const selectedDemandRowRef = useRef(null);
  const [demandTypes, setDemandTypes] = useState([]);
  const [mpOnly, setMpOnly] = useState(true);

  /* MP Order Version Filter */
  const [mpOrderVersionFilter, setMpOrderVersionFilter] = useState(null);
  const mpOrderVersionFilterRef = useRef(null);
  const mpDemandVersionChangingRef = useRef(false);
  const [mpOrderVersionList, setMpOrderVersionList] = useState([]);
  const [mpOrderStatusFilter, setMpOrderStatusFilter] = useState(reduxViewState?.mpOrderStatusFilter ?? []);
  const [mpDemandStatusFilter, setMpDemandStatusFilter] = useState(reduxViewState?.mpDemandStatusFilter ?? []);

  /* Bottom Grid Data */
  const [mpOrderData, setMpOrderData] = useState([]);

  /* Advanced Filter */
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ?? null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  /* Rendering Timing Adjust */
  const [mpDemandGridLoading, setMpDemandGridLoading] = useState(false);
  const [moduleLoading, setModuleLoading] = useState(false);
  const [periodLoading, setPeriodLoading] = useState(false);

  const userData = user;

  const gridCellStyle = (props) => {
    const { value } = props;
    let cellStyle = {};
    if (value === orderStatusCode.ON_TIME) {
      cellStyle = { backgroundColor: statusColor.SUCCESS, fontWeight: 500 };
    } else if (value === orderStatusCode.DELAY) {
      cellStyle = { backgroundColor: statusColor.STOP, fontWeight: 500 };
    } else if (value === orderStatusCode.SHORT) {
      cellStyle = { backgroundColor: statusColor.FAIL, fontWeight: 500 };
    }
    return cellStyle;
  };

  const currencyRenderer = (param, isExport = false) => {
    let symbol = userData?.currencySymbol;
    const currencyCd = param?.data?.currencyCd;
    if (currencyCd) {
      symbol = CodeUtil.getCurrencySymbol(currencyCd);
    }
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };

  const mpDemandGridMemo = useMemo(() => {
    const defaultSettings = DefaultGridSetting({ title, viewName });
    defaultSettings.defaultColDef.filter = true;
    return defaultSettings;
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const mpOrderGridMemo = useMemo(() => {
    const defaultSettings = DefaultGridSetting({ title: `${title}_detail`, viewName: `${viewName}_detail` });
    defaultSettings.defaultColDef.filter = true;
    return defaultSettings;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  
  const orderTypeFormatter = params => {
    const value = params.data?.orderType ?? params.value;
    return orderType?.[value] ? GridUtils.gridValueL10N(orderType[value]) : "";
  }

  const valueFormatter = params => {
    if (params.data?.demandType) {
      return GridUtils.gridValueL10N(params.data.demandType);
    } else if (params?.column?.colDef?.field === "demandType") {
      return GridUtils.gridValueL10N(params.value);
    }

    return "";
  }

  const setToolTip = (version) => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE, PLAN_END_DATE } = version.planConfigObj;

      return `${PLAN_START_DATE} ~ ${PLAN_END_DATE}`
    }
    return null;
  }

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mpDemandColumnDefs = [
    { headerName: "locCd", field: "locCd", type: ["nonEditableColumn"], width: 150, cellClass: 'stringType', filterType: "string" },
    { headerName: "locName", field: "locName", type: ["nonEditableColumn"], width: 150, filterType: "string" },
    { headerName: "reqLocCd", field: "reqLocCd", type: ["nonEditableColumn"], width: 150, cellClass: 'stringType', filterType: "string" },
    { headerName: "reqLocName", field: "reqLocName", type: ["nonEditableColumn"], width: 150, filterType: "string" },
    { headerName: "customerCd", field: "customerCd", type: ["nonEditableColumn"], cellClass: "stringType", width: 150, filterType: "string" },
    { headerName: "customerName", field: "customerName", type: ["nonEditableColumn"], width: 150, filterType: "string" },
    { headerName: "itemCd", field: "itemCd", cellClass: "stringType", type: ["nonEditableColumn"], width: 150, filterType: "string" },
    { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 150, filterType: "string" },
    { headerName: "dueDate", field: "dueDate", type: ["nonEditableColumn"], width: 160, filterType: "date" },
    {
      headerName: "demandType", field: "demandType", type: ["nonEditableColumn"], width: 150,
      valueFormatter, filterValueGetter: valueFormatter,
      filterType: "select", filterOption: Object.values(mpDemandType)
    },
    {
      headerName: "orderType", field: "orderType", type: ["nonEditableColumn"], width: 150,
      /* filterOption 값은 소문자 코드로 준다 - 그 값이 언어팩 키이면서(라벨 번역) 서버 비교가 UPPER 라 DB 값과도 맞는다 */
      valueFormatter: orderTypeFormatter, filterValueGetter: orderTypeFormatter, filterType: "string",
      filterOption: Object.keys(orderType).map(key => key.toLowerCase())
    },
    { headerName: "orderNum", field: "orderNum", type: ["nonEditableColumn"], width: 150, filterType: "string" },
    { headerName: "demandQty", field: "demandQty", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 150, filterType: "number" },
    { 
      headerName: "totalSupplyQty", field: "totalSupplyQty", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 150, 
      headerClass: "right-aligned-custom-header", valueGetter: params => (params.data?.onTimeSupplyQty || 0) + (params.data?.delaySupplyQty || 0), 
      headerComponent: CustomDetailHeader, 
      headerComponentParams: { viewName, detailColumns: [ "onTimeSupplyQty", "onTimeSupplyRate", "delaySupplyQty", "totalSupplyRate" ] } 
    },
    { headerName: "onTimeQty", field: "onTimeSupplyQty", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 150, hide: true },
    { 
      headerName: "onTimeRate", field: "onTimeSupplyRate", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 150, hide: true, 
      valueFormatter: p => p.value != null ? `${NumberUtil.formatNumber(p.value, { maximumFractionDigits: 1 })}%` : "" 
    },
    { headerName: "delaySupplyQty", field: "delaySupplyQty", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 150, hide: true },
    { 
      headerName: "totalRate", field: "totalSupplyRate", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 150, hide: true, 
      valueFormatter: p => p.value != null ? `${NumberUtil.formatNumber(p.value, { maximumFractionDigits: 1 })}%` : "" 
    },
    { headerName: "demandStatus", field: "demandStatus", type: ["nonEditableColumn"],
      valueFormatter: (params) => GridUtils.gridValueL10N(params.value, { headerName: "mpOrderStatus" }), cellStyle: gridCellStyle, width: 150
    },
    { headerName: "uomCd", field: "uomCd", type: ["nonEditableColumn"], width: 100, filterType: "string" },
    { headerName: "demandAmt", field: "demandAmt", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 150, filterType: "number" },
    { headerName: "currencyCd", field: "currencyCd", type: ["nonEditableColumn"], width: 150, sortable: false, filterType: "string" },
    { headerName: "stdSalesPrice", field: "stdUnitPrice", type: ["decimalPointColumn", "nonEditableColumn", "rightAligned"], width: 160, filterType: "number" },
    { headerName: "salesUnitCost", field: "salesUnitCost", type: ["decimalPointColumn", "nonEditableColumn", "rightAligned"], width: 150, filterType: "number" },
    { headerName: "invUnitCost", field: "invUnitCost", type: ["decimalPointColumn", "nonEditableColumn", "rightAligned"], width: 160, filterType: "number" },
    { headerName: "itemPriority", field: "itemPriority", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 150, filterType: "number" },
    { headerName: "customerPriority", field: "customerPriority", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 150, filterType: "number" },
  ];

  const mpOrderColumnDefs = [
    { headerName: "planSeq", field: "planSeqNum", width: 100, type: ["nonEditableColumn"] },
    {
      headerName: "orderCd", field: "orderCd", width: 200, type: ["nonEditableColumn"], cellClass: "stringType",
      cellRenderer: (params) => {
        if (!params.value) return params.value;
        // 주문 추적이 권한 밖이면 링크가 아니라 평문으로 — 밑줄·포인터까지 함께 뺀다.
        if (!isPermitted("ORDER_TRACKING")) return params.value;
        return (
          <span
            style={{ color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}
            onClick={() => onOrderCdClicked(params.value)}
          >
            {params.value}
          </span>
        );
      }
    },
    { headerName: "locName", field: "locName", width: 150, type: ["nonEditableColumn"] },
    { headerName: "itemName", field: "itemName", width: 150, type: ["nonEditableColumn"] },
    { headerName: "dueDate", field: "dueDate", width: 140, type: ["nonEditableColumn"] },
    { headerName: "orderQty", field: "orderQty", width: 120, type: ["nonEditableColumn", "numberColumn", "rightAligned"] },
    { headerName: "supplyQty", field: "shipQty", width: 120, type: ["nonEditableColumn", "numberColumn", "rightAligned"] },
    { headerName: "supplyDate", field: "shipDate", width: 120, type: ["nonEditableColumn"] },
    {
      headerName: "orderStatus", field: "orderStatus", width: 120, type: ["nonEditableColumn"],
      cellStyle: gridCellStyle,
      valueFormatter: (params) => GridUtils.gridValueL10N(params.value, { headerName: "mpOrderStatus" })
    },
    { headerName: "reqLocName", field: "reqLocName", width: 150, type: ["nonEditableColumn"] },
    { headerName: "customerName", field: "customerName", width: 150, type: ["nonEditableColumn"] },
    { headerName: "orderAmt", field: "orderAmt", width: 150, type: ["nonEditableColumn", "currencyNumberColumn", "rightAligned"], valueFormatter: currencyRenderer },
    { headerName: "problemCause", field: "problemCause", width: 150, type: ["nonEditableColumn"] },
    { headerName: "problemDesc", field: "problemDesc", minWidth: 200, type: ["nonEditableColumn"], flex: 1 },
  ];

  const periodCalculate = (version) => {
    let startDate = new ZDate(CURRENT_DATE);
    let endDate = dateUtils.calculateRangeDate(startDate, bucketUnitValue.current, null, planningRangeValue.current, true);

    if (!isEmpty(version)) {
      const selectedVersionPlanConfig = version?.planConfigObj;

      if (selectedVersionPlanConfig) {
        const { PLAN_START_DATE: planStartDate, BUCKET_UNIT: unit, PLANNING_RANGE: range, START_DAY: startDay } = selectedVersionPlanConfig;

        startDate = new ZDate(planStartDate);
        endDate = dateUtils.calculateRangeDate(startDate, unit, startDay, range, true);
      }
    }

    setPeriod({
      start: startDate,
      end: endDate
    });

    setPeriodLoading(true);
  }

  const onChangeVersionHandler = (selectedVersion) => {
    setCurrentPage(1);
    mpDemandVersionChangingRef.current = true;
    mpOrderVersionFilterRef.current = null;
    selectedDemandRowRef.current = null;
    setMpOrderData([]);
    setMpDemandVersionFilter(selectedVersion);
    periodCalculate(selectedVersion);
  }

  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = {};

    if (!isEmpty(mpDemandVersionFilter)) {
      searchFilters.versionId = mpDemandVersionFilter.id;
      searchFilters.moduleCd = mpDemandVersionFilter.moduleCd;
    }

    if (period) {
      searchFilters.startDate = period.start
      searchFilters.endDate = period.end
    }

    if (filterValue?.locations) {
      searchFilters.locationCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    } else if (customerSearch?.current) {
      searchFilters.customer = customerSearch.current;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (demandType) {
      searchFilters.demandType = demandType;
    }

    if (!isEmpty(mpDemandStatusFilter)) {
      searchFilters.demandStatus = mpDemandStatusFilter;
    }

    if (mpOrderVersionFilterRef.current?.id) {
      searchFilters.mpVersionId = mpOrderVersionFilterRef.current.id;
    }

    params.searchFilters = searchFilters;

    if (!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    return params;
  };

    const iconColor = planVersion => {
      if (planVersion) {
        if (isEqual(planVersion.statusCd, RELEASED)) {
          return grey[900];
        }
      }
  
      return green[600];
    }

  const retrieveUserRoles = () => {
    userService.getUserAccessRoles({ username: user.username })
      .then(
        (response) => {
          const userAccess = response.data;
          const userRolesData = userAccess?.roles || [];
          setUserRoles(userAccess?.roles);

          if (userRolesData.includes(roles.APP_RP)) {
            setDemandVersionModule("rp");
          } else if (userRolesData.includes(roles.APP_DP)) {
            setDemandVersionModule("dp");
          }
        },
      )
      .catch(e => { debug(e); });
  };

  const retrieveMpDemandVersions = (mpOnly) => {
    mpDemandService.getMpDemandVersionsHavingMpVersion()
    .then(response => {
      if (response.data) {
        const { data } = response;

        /* 버전 조회시 Mp Settings 데이터 셋팅 */
        planningRangeValue.current = data?.[PLANNING_RANGE] ? Number(data[PLANNING_RANGE]) : 120;
        bucketUnitValue.current = data?.[BUCKET_UNIT] || buckets.DAY;

        if (mpOnly) {
          periodCalculate(null);

          setMpDemandVersionFilter(null);
          setMpDemandVersionList([]);
          return;
        }

        if (isEmpty(data?.results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });

          setMpDemandVersionFilter(null);
          setMpDemandVersionList([]);
        } else {
          const mpDemandVersion = reduxViewState?.mpDemandVersion || data.results[0];
          
          periodCalculate(mpDemandVersion);

          setMpDemandVersionFilter(mpDemandVersion);
          setMpDemandVersionList(data.results);
        }
      }
    })
    .catch(e => {
      const { data } = e.response;
      if (data?.message) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t(data.message) });
      }
      debug(e);
    })

  }

  const retrieveMpDemand = () => {
    mpDemandGridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams();

    mpDemandService.get(params)
      .then(response => {
        if (response.data) {
          const { results, totalPages } = response.data;
          setMpDemandData((results ?? []).map(row => {
            const onTimeSupplyQty = row.onTimeSupplyQty ?? 0;
            const delaySupplyQty = row.delaySupplyQty ?? 0;
            const demandQty = row.demandQty || 0;
            return {
              ...row,
              demandStatus: row.demandStatus ?? orderStatusCode.SHORT,
              onTimeSupplyQty,
              delaySupplyQty,
              onTimeSupplyRate: demandQty ? (onTimeSupplyQty / demandQty) * 100 : 0,
              totalSupplyRate: demandQty ? ((onTimeSupplyQty + delaySupplyQty) / demandQty) * 100 : 0,
            };
          }));
          setGridPages(totalPages ?? 1);

          if (isEmpty(results)) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          }
        }
      })
      .catch(e => {
        const { data } = e.response;
        if (data?.message) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t(data.message) });
        }
        debug(e);
      })
      .then(() => {
        const reduxFilterValue = { mpDemandVersion: mpDemandVersionFilter, customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, demandType, userRoles, filter: filterValue, advancedFilters, currentPage, pageSize, mpOrderVersion: mpOrderVersionFilterRef.current, mpOrderStatusFilter, mpDemandStatusFilter };
        const reduxState = reduxUtil.createObjectState(viewName, reduxFilterValue);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .finally(() => {
        mpDemandGridRef.current?.api?.setGridOption('loading', false);
      });
  };

  const demandTypeAndModuleHandler = (userRoles) => {
    const demandTypeArray = [];
    
    let isMpModuleOnly = true;
    if (userRoles.includes(roles.APP_RP)) {
      demandTypeArray.push(REPLENISHMENT);

      isMpModuleOnly = false;
    } else if (userRoles.includes(roles.APP_DP)) {
      demandTypeArray.push(FORECAST);

      isMpModuleOnly = false;
    }


    demandTypeArray.push(PRODORDER);

    setDemandTypes(demandTypeArray);
    setMpOnly(isMpModuleOnly);
    setModuleLoading(true);
  }

  const orderStatusOnChangeHandler = (_, newValue) => {
    setMpOrderStatusFilter(newValue);
  };

  const demandStatusOnChangeHandler = (_, newValue) => {
    setMpDemandStatusFilter(newValue);
  };

  const getChipColor = (statusCode) => {
    switch(statusCode) {
      case orderStatusCode.ON_TIME:
        return statusColor.SUCCESS;
      case orderStatusCode.DELAY:
        return statusColor.STOP;
      case orderStatusCode.SHORT:
        return statusColor.FAIL;
      default:
        return statusColor.IDLE;
    }
  };

  const onOrderCdClicked = (orderCd) => {
    const ORDER_TRACKING = "ORDER_TRACKING";
    reduxDispatch(reduxUtil.removeViewState({ viewName: ORDER_TRACKING }));
    const reduxState = reduxUtil.createObjectState(ORDER_TRACKING, { orderCd, mpVersion: mpOrderVersionFilterRef.current });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
    reduxDispatch(reduxUtil.updateHistoryState(ORDER_TRACKING));
  };

  const retrieveMpOrders = (rowData) => {
    const requestParams = {
      rpVersionId: mpDemandVersionFilter?.id,
      mpVersionId: mpOrderVersionFilter?.id,
      locationCd: rowData?.locCd,
      reqLocationCd: rowData?.reqLocCd,
      itemCd: rowData?.itemCd,
      customerCd: rowData?.customerCd,
      dueDate: rowData?.dueDate,
      orderType: rowData?.demandType,
      orderQty: rowData?.demandQty,
      orderNum: rowData?.orderNum,
    };

    if (!isEmpty(mpOrderStatusFilter)) {
      requestParams.orderStatus = mpOrderStatusFilter;
    }

    mpOrderGridRef.current?.api?.setGridOption('loading', true);
    mpDemandService.getMpOrdersByDemand(requestParams)
      .then(response => {
        const localizedResults = applyLangPackToProblemDesc(response.data?.results, t);
        setMpOrderData(localizedResults ?? []);
      })
      .catch(e => { debug(e); })
      .finally(() => {
        mpOrderGridRef.current?.api?.setGridOption('loading', false);
      });
  };

  const onMpDemandSelectionChanged = useCallback((params) => {
    const selectedRows = params.api.getSelectedRows();
    const rowData = selectedRows[0] ?? null;

    selectedDemandRowRef.current = rowData;

    if (!rowData) {
      setMpOrderData([]);
      return;
    }

    retrieveMpOrders(rowData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpDemandVersionFilter, mpOrderVersionFilter, mpOrderStatusFilter]);

  const onMpDemandRowDataUpdated = useCallback((params) => {
    const firstRow = params.api.getDisplayedRowAtIndex(0);

    if (!firstRow) {
      setMpOrderData([]);
      return;
    }

    params.api.deselectAll();
    firstRow.setSelected(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retrieveMpOrderVersions = () => {
    planVersionService.getAll({ moduleCd: MP, statusCds: [PLANNED, RELEASED], orderByColumn: "updatedTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        if (results) {
          const filtered = mpDemandVersionFilter?.id
            ? results.filter(v => v.planConfigObj?.RP_VERSION_ID === mpDemandVersionFilter.id)
            : results;

          setMpOrderVersionList(filtered);
          const savedMpOrderVersion = reduxViewState?.mpOrderVersion;
          const restoredVersion = savedMpOrderVersion
            ? (filtered.find(v => v.id === savedMpOrderVersion.id) ?? filtered[0] ?? null)
            : (filtered[0] ?? null);
          const previousVersion = mpOrderVersionFilter;
          const wasChanging = mpDemandVersionChangingRef.current;
          mpOrderVersionFilterRef.current = restoredVersion;
          mpDemandVersionChangingRef.current = false;
          setMpOrderVersionFilter(restoredVersion);

          if (wasChanging && restoredVersion?.id === previousVersion?.id) {
            if (mpDemandGridLoading && (!isEmpty(mpDemandVersionFilter) || mpOnly) && period && moduleLoading && periodLoading) {
              retrieveMpDemand();
            }
          }
        }
      })
      .catch(e => { debug(e); });
  };

  const onMpOrderGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs: mpOrderColumnDefs, viewName: `${viewName}_detail`, initState: true });
  };

  const onMpDemandGridReady = params => {
    setMpDemandGridLoading(true);

    GridUtils.setColumnDefs({ ...params, columnDefs: mpDemandColumnDefs, viewName, initState: true });
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveMpDemand();
    }
  }

  const applyLangPackToProblemDesc = (results, t) => {
    if (!Array.isArray(results)) return results;
    return results.map(item => {
      if (typeof item.problemDesc === 'string') {
        item.problemDesc = item.problemDesc.replace(/\$\{([^}]+)\}/g, (match, key) => t(key));
      }
      return item;
    });
  };

  // STEP1 Effect
  useEffect(() => {
    if (isEmpty(userRoles)) {
      retrieveUserRoles();
    } else {
      demandTypeAndModuleHandler(userRoles); // 사용하는 Module 및 Demand Type useState 설정 
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles])

  // STEP2 Effect
  useEffect(() => {
    if (!isEmpty(demandTypes)) {
      retrieveMpDemandVersions(mpOnly);
    } 

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demandTypes])

  // STEP3 Effect
  useEffect(() => {
    if (mpDemandVersionChangingRef.current)
      return;

    if (userRoles && mpDemandGridLoading && (!isEmpty(mpDemandVersionFilter) || mpOnly) && period && moduleLoading && periodLoading) {
      retrieveMpDemand();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpDemandVersionFilter, mpOrderVersionFilter, filterValue, demandType, period, moduleLoading, periodLoading]);

  // MP Order Version Filter Effect - RP 버전 확정 이후 MP 버전 목록 세팅/재필터링
  useEffect(() => {
    if (!periodLoading) return;
    retrieveMpOrderVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpDemandVersionFilter, periodLoading]);

  useEffect(() => {
    if (userRoles && mpDemandGridLoading && (!isEmpty(mpDemandVersionFilter) || mpOnly) && period && moduleLoading && periodLoading) {
      retrieveMpDemand();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, mpDemandGridLoading]);
  // Advanced Filter 변경 시 버튼 색상 업데이트 및 재조회
  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }

    if (mpDemandGridLoading) {
      initPageToRetrieve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  // mpOrderVersionFilter ref 동기화 (retrieveMpDemand에서 최신값 참조용)
  useEffect(() => {
    mpOrderVersionFilterRef.current = mpOrderVersionFilter;
  }, [mpOrderVersionFilter]);

  // MP Order 버전/상태 필터 변경 시 하단 그리드 재조회 및 Redux 저장
  useEffect(() => {
    if (!mpDemandGridLoading) return;

    // demandStatus 필터 활성 시 mpOrderVersionFilter 변경은 backend에서 demandStatus를 mpVersionId 기준으로 재계산하므로 상단 그리드 재조회 필요
    if (!isEmpty(mpDemandStatusFilter) && mpOrderVersionFilter) {
      initPageToRetrieve();
      return;
    }

    if (selectedDemandRowRef.current) {
      retrieveMpOrders(selectedDemandRowRef.current);
    }

    const reduxFilterValue = { mpDemandVersion: mpDemandVersionFilter, customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, demandType, userRoles, filter: filterValue, advancedFilters, currentPage, pageSize, mpOrderVersion: mpOrderVersionFilterRef.current, mpOrderStatusFilter, mpDemandStatusFilter };
    const reduxState = reduxUtil.createObjectState(viewName, reduxFilterValue);
    reduxDispatch(reduxUtil.updateViewState(reduxState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpOrderVersionFilter, mpOrderStatusFilter]);

  // Demand Status 필터 변경 시 상단 그리드 재조회
  useEffect(() => {
    if (!mpDemandGridLoading) return;
    initPageToRetrieve();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpDemandStatusFilter]);

  return (
    <>
      <FilterContainer>
        <Box id="rp-dp-demand-ver">
          <VersionAutocomplete
            options={mpDemandVersionList}
            value={!isNull(mpDemandVersionFilter) ? mpDemandVersionFilter : null}
            onChange={(_, newValue) => onChangeVersionHandler(newValue)}
            label={`${t(demandVersionModule)} ${t("version")}`}
            getIcon={(option) => (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon)}
            getIconColor={iconColor}
            getTooltip={setToolTip}
            disabled={mpOnly}
            inputIconSx={{ mr: 1 }}
          />
        </Box>
        <PeriodFilter period={period} setPeriod={setPeriod} />
        <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <CustomerAutocomplete
          retrieve={initPageToRetrieve}
          searchValue={customerSearch}
          extraCodesRef={extraCustomerCodesRef}
        />
        <MPItemAutocomplete
          retrieve={initPageToRetrieve}
          searchValue={itemSearch}
          extraCodesRef={extraItemCodesRef}
        />
        <Box id="mp-demand-auto">
          <Autocomplete
            options={demandTypes}
            getOptionLabel={option => t(option)}
            value={demandType}
            onChange={(_, newValue) => setDemandType(newValue)}
            isOptionEqualToValue={(option, value) => option === value}
            size="small"
            sx={{ minWidth: 230, width: 240, mr: 1 }}
            renderInput={(params) => (
              <TextField {...params} label={t("demandType")} />
            )}
          />
        </Box>
        <Autocomplete
          key={"demand-status-auto-complete"}
          options={STATUS_FILTER_DATA_OPTIONS}
          getOptionLabel={option => {
            return GridUtils.gridValueL10N(option, { headerName: "mpOrderStatus" });
          }}
          value={mpDemandStatusFilter}
          onChange={demandStatusOnChangeHandler}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          sx={{ mr: 1, minWidth: 240, maxWidth: 360 }}
          multiple
          filterSelectedOptions
          autoCapitalize="none"
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                key={option}
                label={GridUtils.gridValueL10N(option, { headerName: "mpOrderStatus" })}
                {...getTagProps({ index })}
                size="small"
                style={{ backgroundColor: getChipColor(option), fontWeight: 500 }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label={t("demandStatus")} />
          )}
        />
        <Box sx={{ ml: "auto" }}>
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={() => setOpenFilter(!openFilter)} />
          <DownloadButton label="exportAll" onClick={() => mpDemandGridRef.current.api.exportDataAsExcel()} />
        </Box>
      </FilterContainer>
      <Box className="grid-area" sx={{ display: "flex", flexDirection: "row"}}>
        <Box sx={{ flex: 1 }}>
        <SplitterContainer initialSizes={[1.2, 1]} collapseDirection='up'>
          <Box height="100%" sx={{ display: "flex", flexDirection: "row" }}>
            <Box height="100%" sx={{ flex: 1 }}>
              <Box className="ag-theme-balham" height="calc(100% - 50px)" sx={{ mb: 1 }}>
                <AgGridReact
                  ref={mpDemandGridRef}
                  rowData={mpDemandData}
                  {...mpDemandGridMemo}
                  statusBar={statusBar}
                  onGridReady={onMpDemandGridReady}
                  onRowDataUpdated={onMpDemandRowDataUpdated}
                  onSelectionChanged={onMpDemandSelectionChanged}
                />
              </Box>
              <PaginationContainer
                currentPage={currentPage} totalPages={gridPages}
                pageSize={pageSize} pageSizes={pageSizes}
                setCurrentPage={setCurrentPage} setPageSize={setPageSize}
              />
            </Box>
          </Box>
          <Box height="100%" sx={{ display: "flex", flexDirection: "column" }}>
            <FilterContainer hasIcons={false} sx={{ pt: 2 }}>
              <Box id="mp-order-ver">
                <VersionAutocomplete
                  options={mpOrderVersionList}
                  value={mpOrderVersionFilter}
                  onChange={(_, newValue) => {
                    mpOrderVersionFilterRef.current = newValue;
                    setMpOrderVersionFilter(newValue);
                  }}
                  label={`MP ${t("version")}`}
                  getIcon={(option) => (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon)}
                  getIconColor={iconColor}
                  getTooltip={setToolTip}
                  inputIconSx={{ mr: 1 }}
                />
              </Box>
              <Autocomplete
                key={"order-status-auto-complete"}
                options={STATUS_FILTER_DATA_OPTIONS}
                getOptionLabel={option => {
                  return GridUtils.gridValueL10N(option, { headerName: "mpOrderStatus" });
                }}
                value={mpOrderStatusFilter}
                onChange={orderStatusOnChangeHandler}
                isOptionEqualToValue={(option, value) => option === value}
                size="small"
                sx={{ mr: 1, minWidth: 240, maxWidth: 360 }}
                multiple
                filterSelectedOptions
                autoCapitalize="none"
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      key={option}
                      label={GridUtils.gridValueL10N(option, { headerName: "mpOrderStatus" })}
                      {...getTagProps({ index })}
                      size="small"
                      style={{ backgroundColor: getChipColor(option), fontWeight: 500 }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label={t("orderStatus")} />
                )}
              />
            </FilterContainer>
            <Box className={"ag-theme-balham"} sx={{ flex: 1 }}>
              <AgGridReact
                ref={mpOrderGridRef}
                rowData={mpOrderData}
                {...mpOrderGridMemo}
                statusBar={statusBar}
                onGridReady={onMpOrderGridReady}
              />
            </Box>
          </Box>
        </SplitterContainer>
        </Box>
        {openFilter && (
          <ResizableFilterPanel>
            <AdvancedFilter
              columnDefs={mpDemandColumnDefs}
              advancedFilters={advancedFilters}
              onApply={setAdvancedFilters}
              onClose={() => setOpenFilter(false)}
            />
          </ResizableFilterPanel>
        )}
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(MpDemandAnalysis);